import { ApiError, unwrap, type Schemas } from '@swarm/api-client';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export type Entry = Schemas['EntryDto'];
export type EntryKind = Schemas['EntryKindDto'];
export type FinanceCategory = Schemas['FinanceCategoryDto'];
export type FinanceOverview = Schemas['FinanceOverviewDto'];
export type FinanceMethod = Schemas['FinancePaymentMethodDto'];
export type SaveEntryRequest = Schemas['SaveEntryRequest'];

export interface EntryFilter {
  from?: string | undefined;
  to?: string | undefined;
  kind?: EntryKind | undefined;
  category_id?: string | undefined;
  include_cancelled?: boolean | undefined;
}

const keys = {
  all: ['finance'] as const,
  entries: (f: EntryFilter) => ['finance', 'entries', f] as const,
  entry: (id: string) => ['finance', 'entry', id] as const,
  categories: ['finance', 'categories'] as const,
  overview: (year: number | undefined) => ['finance', 'overview', year ?? 'current'] as const,
};

function invalidate(client: QueryClient) {
  return client.invalidateQueries({ queryKey: keys.all });
}

const ifMatch = (version?: number): Record<string, string> =>
  version === undefined ? {} : { 'If-Match': `"${version}"` };

function query(f: EntryFilter) {
  return {
    ...(f.from ? { from: f.from } : {}),
    ...(f.to ? { to: f.to } : {}),
    ...(f.kind ? { kind: f.kind } : {}),
    ...(f.category_id ? { category_id: f.category_id } : {}),
    include_cancelled: f.include_cancelled ?? false,
  };
}

export function useEntries(filter: EntryFilter = {}, enabled = true) {
  return useQuery({
    queryKey: keys.entries(filter),
    queryFn: async () =>
      unwrap(await api.GET('/finance/entries', { params: { query: query(filter) } })),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useEntry(id: string) {
  return useQuery({
    queryKey: keys.entry(id),
    queryFn: async () =>
      unwrap(await api.GET('/finance/entries/{entry_id}', { params: { path: { entry_id: id } } })),
  });
}

/** The CSV export of the filter (served with the session cookie). */
export function exportUrl(filter: EntryFilter): string {
  const params = new URLSearchParams(Object.entries(query(filter)).map(([k, v]) => [k, String(v)]));
  return `/api/v1/finance/export?${params.toString()}`;
}

export function receiptUrl(id: string, version: number): string {
  return `/api/v1/finance/entries/${id}/receipt?v=${version}`;
}

export function useSaveEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      kind,
      body,
      version,
    }: {
      id?: string | undefined;
      kind: EntryKind;
      body: SaveEntryRequest;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/finance/entries/{entry_id}', {
              params: { path: { entry_id: id } },
              body,
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/finance/entries', { body: { ...body, kind } })),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteEntry() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.DELETE('/finance/entries/{entry_id}', { params: { path: { entry_id: id } } }),
      ),
    onSuccess: () => invalidate(client),
  });
}

/** Uploads (file) or removes (null) the receipt of an entry (FIN-004). */
export function useReceipt() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File | null }) => {
      const url = file
        ? `/api/v1/finance/entries/${id}/receipt?filename=${encodeURIComponent(file.name)}`
        : `/api/v1/finance/entries/${id}/receipt`;
      const response = await fetch(url, {
        method: file ? 'PUT' : 'DELETE',
        credentials: 'include',
        headers: {
          'X-Swarm-Csrf': '1',
          'Idempotency-Key': crypto.randomUUID(),
          ...(file ? { 'Content-Type': file.type || 'application/octet-stream' } : {}),
        },
        body: file ?? null,
      });
      if (!response.ok) {
        throw new ApiError(response.status, await response.json().catch(() => undefined));
      }
      return (await response.json()) as Entry;
    },
    onSuccess: () => invalidate(client),
  });
}

export function useFinanceCategories(enabled = true) {
  return useQuery({
    queryKey: keys.categories,
    queryFn: async () => unwrap(await api.GET('/finance/categories')),
    enabled,
  });
}

export function useSaveFinanceCategory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      kind,
      name,
      archived,
      version,
    }: {
      id?: string | undefined;
      kind: EntryKind;
      name: string;
      archived?: boolean | undefined;
      version?: number | undefined;
    }) =>
      id
        ? unwrap(
            await api.PUT('/finance/categories/{category_id}', {
              params: { path: { category_id: id } },
              body: { name, archived: archived ?? false },
              headers: ifMatch(version),
            }),
          )
        : unwrap(await api.POST('/finance/categories', { body: { kind, name } })),
    onSuccess: () => invalidate(client),
  });
}

export function useFinanceOverview(year?: number) {
  return useQuery({
    queryKey: keys.overview(year),
    queryFn: async () =>
      unwrap(
        await api.GET('/finance/overview', {
          params: { query: year ? { year } : {} },
        }),
      ),
    placeholderData: (previous) => previous,
  });
}
